import './dayjsConfig';
import './pdfjs';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';

import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import AntdProvider from 'AntdProvider';
import AxiosProvider from 'api/AxiosProvider';
import MaintenanceProvider from 'common/MaintenanceProvider';
import type { ApiError } from 'common/models';
import { DEFAULT_QUERY_STALE_TIME } from 'common/queries';
import { AuthProvider } from 'common/useAuth';
import SuspenseLoadingIndicator from 'components/LoadingPageIndicator/SuspenseLoadingIndicator';
import { getEnvironmentName } from 'helpers/environmentHelpers';
import { handleApiError } from 'helpers/errorHandling';
import React, { Suspense, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from 'react-router-dom';

import App from './App';
import { i18nInit } from './i18n';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: DEFAULT_QUERY_STALE_TIME,
      refetchOnReconnect: true,
      onError: (error) => handleApiError(error),
      retry: (count, error) => {
        const err = error as ApiError;
        if (!err.response) return true;

        const status = err.response.status;

        // Max retry 3, 503 is server Maintenance
        if (count >= 2 || status === 503) {
          return false;
        }

        return status >= 500 || status === 429;
      }
    },
    mutations: {
      onError: (error) => handleApiError(error),
      retry: (count, error) => {
        const err = error as ApiError;
        if (!err.response) return true;

        const status = err.response.status;

        // Max retry 3, 503 is server Maintenance
        if (count >= 2 || status === 503) {
          return false;
        }

        return status >= 500 || status === 429;
      }
    }
  }
});

Sentry.init({
  dsn: 'https://33ec798928dc40468c03bc092d47f15f@o1160602.ingest.sentry.io/6245145',
  release: __BUILD_VERSION__,
  environment: getEnvironmentName(),
  // This enables automatic instrumentation (highly recommended), but is not
  // necessary for purely manual usage
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    }),
    Sentry.feedbackIntegration({
      // Add configuration if needed
      colorScheme: 'light',
      autoInject: false // We're using captureFeedback manually
    })
  ],
  ignoreErrors: ['ResizeObserver loop limit exceeded'],
  // To set a uniform sample rate
  tracesSampleRate: 0.25, // TODO: do not use 1 in production!!!
  tracePropagationTargets: [
    'localhost',
    'app.mwork365.com',
    'staging.mwork365.cloud',
    'test.mwork365.cloud'
  ]
});

const container = document.getElementById('app');
const root = createRoot(container!);

root.render(
  <I18nextProvider i18n={i18nInit}>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={<SuspenseLoadingIndicator />}>
          <AuthProvider>
            <AntdProvider>
              <MaintenanceProvider>
                <AxiosProvider>
                  <Suspense fallback={<SuspenseLoadingIndicator />}>
                    <App />
                    <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
                  </Suspense>
                </AxiosProvider>
              </MaintenanceProvider>
            </AntdProvider>
          </AuthProvider>
        </Suspense>
      </QueryClientProvider>
    </BrowserRouter>
  </I18nextProvider>
);
